import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Box, Container } from '@radix-ui/themes'

import { useConsignmentFetcher } from '../hooks/useConsignmentFetcher'
import ConsignmentDet from '../interfaces/ConsignmentDet'
import { formatWithThousandSep } from '../utils/utils'
import { AppPath } from '../enums/AppPath'
import { Rule, validateFields, validateNotEqual } from '../utils/validateForm'
import useConsignmentVoid from '../hooks/useConsignmentVoid'
import useConsignmentArchive from '../hooks/useConsignmentArchive'
import useConsignmentPrice from '../hooks/useConsignmentPrice'
import { ScreenWrapper } from '../modules/common/components/ScreenWrapper'

import DocFooter from './DocFooter'
import InputField from './InputField'
import { DisplayValueVert } from './DisplayValue'
import { ButtonCheckConfirma, ButtonMenuSet, ButtonPrint } from './Buttons'
import DocConfirma from './DocConfirma'
import ConsignmentDetail from './ConsignmentDetail'
import { Alert, Info } from './MessageInfo'
import { PrintableCard } from './PrintableCard'

const ConsignmentDetailPage: React.FC = () => {
  const [consignment, setConsignment] = useState<ConsignmentDet | null>(null)
  const { errorConsignment, fetchConsignment } = useConsignmentFetcher()
  const [clickedIndex, setClickedIndex] = useState<number>(-1)
  const [priceFormat, setPriceFormat] = useState<string>('')
  // Al siguiente le ponemos true al principio para evitar un parpadeo
  const [isInactiva, setIsInactiva] = useState<boolean>(true)
  const [refreshPage, setRefreshPage] = useState<boolean>(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const { errorConsignmentVoid, consignmentVoid } = useConsignmentVoid()
  const { errorConsignmentArchive, consignmentArchive } = useConsignmentArchive()
  const { loading: loadingPrice, consignmentPrice } = useConsignmentPrice()
  const navigate = useNavigate()

  const [motivo, setMotivo] = useState<string>('')
  const [infoMessage, setInfoMessage] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')

  // Extracting id parameter from the route
  const { id } = useParams<{ id: string }>()
  const currentId = id ?? ''

  useEffect(() => {
    fetchDataAsync()
  }, [currentId])

  useEffect(() => {
    if (refreshPage) fetchDataAsync()
    setRefreshPage(false)
  }, [refreshPage])

  const fetchDataAsync = async () => {
    if (currentId === '') {
      return // Exit early if `id` is undefined
    }
    const consignment = await fetchConsignment(currentId)

    if (errorConsignment) {
      setErrorMessage(errorConsignment.message)

      return
    }
    setConsignment(consignment)

    setIsInactiva(!!consignment?.voidedAt || !!consignment?.archivedAt || !!consignment?.expiredAt)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target

    if (name === 'price_format') {
      let numericValue = value.replace(/[^\d]/g, '') // Strip non-numeric characters

      if (!numericValue) {
        numericValue = '0'
      }

      const num = Number(numericValue)
      const num_format = formatWithThousandSep(num)
      setPriceFormat(num_format)
    } else if (name === 'motivo') {
      setMotivo(value)
    }
  }

  const price_client_format = formatWithThousandSep(consignment?.price ?? 0)

  const names = {
    profileName: consignment?.profile_name ?? '',
    organizationName: consignment?.organization.name ?? '',
    clientName: `${consignment?.client.firstname}  ${consignment?.client.lastname}`,
  }

  const handleCambiarClick = async () => {
    if (!currentId || loadingPrice) return

    let numericValue = priceFormat.replace(/[^\d]/g, '') // Strip non-numeric characters

    if (!numericValue) {
      numericValue = '0'
    }
    const num = Number(numericValue)
    const fields = [{ name: 'price_format', value: num, rule: Rule.GREATER_ZERO }]

    const validationErrors = validateFields(fields)

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors) // Set errors if any

      return
    }
    const fieldError = validateNotEqual(
      'price_format',
      priceFormat,
      price_client_format,
      'Nuevo precio no puede ser igual al antiguo'
    )

    if (Object.keys(fieldError).length > 0) {
      setErrors(fieldError) // Set errors if any

      return
    }
    setErrors({})
    const price = Number(numericValue)
    const updatedId = await consignmentPrice(currentId, price)

    if (!updatedId) {
      setErrorMessage('No se pudo cambiar el precio cliente.')
      setRefreshPage(true)

      return
    }
    setPriceFormat('')
    // Si nos va bien, volvemos a la ventana de detalle (esta misma).
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setRefreshPage(true) // Re-fetch data after the successful operation
    setInfoMessage('Precio modificado.')
  }

  const handleAnularClick = async (motivo: string) => {
    if (!currentId) return

    await consignmentVoid(currentId, motivo)

    if (errorConsignmentVoid) {
      setErrorMessage(errorConsignmentVoid.message)

      return
    }
    // Si nos va bien, volvemos a la ventana de detalle.
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setRefreshPage(true) // Re-fetch data after the successful operation
    setInfoMessage('Consignación anulada.')
  }

  const handleArchivarClick = async (motivo: string) => {
    if (!currentId) return

    await consignmentArchive(currentId, motivo)

    if (errorConsignmentArchive) {
      setErrorMessage(errorConsignmentArchive.message)

      return
    }
    // Si nos va bien, redirigimos al listado de consignaciones archivadas
    setInfoMessage('Consignación archivada.')
    navigate(`/${AppPath.Consignments}?select=Archivadas`)
  }

  const alertMessageTimeout = (): void => {
    setErrorMessage('')
  }

  const infoMessageTimeout = (): void => {
    setInfoMessage('')
  }

  return (
    <ScreenWrapper>
      {errorMessage !== '' && <Alert message={errorMessage} trigger={Date.now()} onTimeout={alertMessageTimeout} />}
      {infoMessage !== '' && <Info message={infoMessage} trigger={Date.now()} onTimeout={infoMessageTimeout} />}
      {/*preguntamos por consignment para que no parpadee */}
      {consignment && (
        <Container size="3">
          <PrintableCard>
            <ConsignmentDetail consig={consignment} />
            <DocFooter names={names} />
          </PrintableCard>
          <Box my="2">
            <ButtonPrint />
          </Box>
        </Container>
      )}
      {!isInactiva && (
        <div>
          <ButtonMenuSet
            captions={['Actualizar Precio Cliente', 'Anular', 'Archivar']}
            clickedIndex={clickedIndex}
            handleButtonClick={(n) => setClickedIndex(n)}
            short={true}
          />
          <div className="mt-8">
            {clickedIndex === 0 && (
              <>
                <div>
                  Esto actualizará el precio cliente de esta consignación al valor ingresado. El contrato mostrará el
                  nuevo precio.
                </div>
                <div className="grid grid-cols-4 gap-4 m-4">
                  <DisplayValueVert
                    label="Precio cliente actual"
                    value={price_client_format}
                    valueClass="bg-neutral-200 max-w-40"
                  />
                  <div className="max-w-40">
                    <InputField
                      label="Nuevo precio cliente"
                      name="price_format"
                      value={priceFormat}
                      type="string"
                      handleInputChange={handleInputChange}
                      errors={errors}
                    />
                  </div>
                </div>
                <DocConfirma
                  docType={AppPath.Consignments}
                  action={1}
                  handleCrearClick={handleCambiarClick}
                  loading={loadingPrice}
                />
              </>
            )}
            {clickedIndex === 1 && (
              <div>
                <ButtonCheckConfirma
                  title="Anular Consignación"
                  subTitle="Al anular la consignación esta ya no será visible y no será legalmente válida."
                  checkLabel="Estoy seguro de anular esta consignación."
                  buttonLabel="Anular"
                  handleCrearClick={() => handleAnularClick(motivo)}
                  handleTextAreaChange={handleInputChange}
                  textAreaLabel="Motivo de anulación"
                  textAreaName="motivo"
                  textAreaValue={motivo}
                />
              </div>
            )}
            {clickedIndex === 2 && (
              <div>
                <ButtonCheckConfirma
                  title="Archivar Consignación"
                  subTitle="Al archivar la consignación esta dejará de estar activa pero se conservará todo el historial."
                  checkLabel="Estoy seguro de archivar esta consignación."
                  buttonLabel="Archivar"
                  handleCrearClick={() => handleArchivarClick(motivo)}
                  handleTextAreaChange={handleInputChange}
                  textAreaLabel="Motivo de archivo"
                  textAreaName="motivo"
                  textAreaValue={motivo}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </ScreenWrapper>
  )
}

export default ConsignmentDetailPage
